import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiUpload } from "../../api/client";
import { buildListQuery } from "../../api/listQuery";
import { buildAvatarForm } from "../../utils/resizeImage";
import { ME_QUERY_KEY } from "./useMe";

const computeAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return 0;
  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return 0;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age > 0 ? age : 0;
};

// Normalized athlete row for scout lists. New athletes store sport/position/
// birth_date/country only inside `attributes`, so both sources are coalesced.
export const normalizeAthleteRecord = (row) => {
  const attributes = row?.attributes || {};
  const birthDate = row?.birth_date || attributes.birth_date || null;
  const sport = row?.sport || attributes.sport || "";
  const position = row?.position || attributes.main_position || attributes.position || "";
  const age =
    Number(attributes.age) > 0 ? Number(attributes.age) : computeAgeFromBirthDate(birthDate);

  return {
    id: row?.id,
    athlete_id: row?.id,
    full_name: row?.full_name || "Sin nombre",
    email: row?.email || "",
    avatar_url: attributes.avatar_url || row?.avatar_url || "",
    sport,
    position,
    category: row?.category || attributes.category || "Amateur",
    country: row?.country || attributes.country || "",
    age,
    height: Number(attributes.height) || 0,
    weight: Number(attributes.weight) || 0,
    points: Number(attributes.points) || 0,
    birth_date: birthDate,
    created_at: row?.created_at,
    attributes,
    profile_complete: Boolean(birthDate && sport && position),
  };
};

// The backend caps pageSize at 100 (lib/pagination.ts).
const MAX_PAGE_SIZE = 100;

const fetchAthletesPage = (params, page) =>
  apiFetch("/athletes", {
    query: buildListQuery(page, {
      sort: "created_at",
      order: "desc",
      search: params.search,
      sport: params.sport,
      ...params.filters,
    }, { pageSize: params.pageSize ?? MAX_PAGE_SIZE }),
  });

// Scout-side athlete list (all registered athletes, most recent first). With
// no `page` it walks every page: stopping at the first 100 hid the older, real
// accounts behind newer ones (e.g. a demo seed).
export const useAthletes = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["athletes", params],
    queryFn: async () => {
      if (params.page) return fetchAthletesPage(params, params.page);
      const first = await fetchAthletesPage(params, 1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, (first.pages ?? 1) - 1) }, (_, i) => fetchAthletesPage(params, i + 2)),
      );
      // A signup between page fetches shifts rows by one; drop the repeat.
      const seen = new Set();
      const items = [first, ...rest]
        .flatMap((p) => p.items || [])
        .filter((row) => !seen.has(row.id) && seen.add(row.id));
      return { ...first, items, page: 1, pages: 1, pageSize: items.length };
    },
    select: (data) => ({ ...data, items: (data.items || []).map(normalizeAthleteRecord) }),
    ...options,
  });

export const useAthlete = (id, options = {}) =>
  useQuery({
    queryKey: ["athletes", id],
    queryFn: () => apiFetch(`/athletes/${id}`),
    enabled: Boolean(id) && (options.enabled ?? true),
  });

export const useUpdateAthlete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) =>
      apiFetch(`/athletes/${id}`, { method: "PATCH", body: fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["athletes"] });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
};

// Server-side jsonb merge of attributes.consents (no read-merge-write race).
export const useSaveConsents = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ athleteId, consents }) =>
      apiFetch(`/athletes/${athleteId}/consents`, { method: "POST", body: consents }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      // athleteData (AuthContext) reads the athlete row, where consents live.
      queryClient.invalidateQueries({ queryKey: ["athletes"] });
      // Coach reports redact per consent; nothing cached may outlive a change.
      queryClient.invalidateQueries({ queryKey: ["report"] });
    },
  });
};

// Profile photo -> R2 through the backend. The server stores a 512 px WebP and
// writes attributes.avatar_url itself, so nothing else needs PATCHing.
export const useUploadAthleteAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => apiUpload(`/athletes/${id}/avatar`, await buildAvatarForm(file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["athletes"] });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
};
